import React from 'react';
import { Link } from 'react-router-dom';

interface Service {
  name: string;
  description: string;
  icon: React.ReactNode;
  href?: string;
}

const services: Service[] = [
  {
    name: 'E-commerce Website Development',
    description: 'A standalone online store with catalog, checkout, payments, and order management.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
    href: '/services/e-commerce',
  },
  {
    name: 'Custom Software Development',
    description: 'Tailored software solutions designed specifically for your business needs.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
  {
    name: 'Web Development',
    description: 'Professional websites for machine shops and manufacturing businesses.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    href: '/services/web-development',
  },
  {
    name: 'Mobile App Development',
    description: 'Native and cross-platform mobile apps for iOS and Android.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    name: 'Cloud Solutions',
    description: 'Cloud architecture, migration, and management on AWS, Azure, and GCP.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
  },
  {
    name: 'UI/UX Design',
    description: 'User-centered design that combines aesthetics with functionality.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    ),
  },
];

const ServicesSection: React.FC = () => {
  return (
    <section id="services" className="py-24 bg-slate-50 dark:bg-dark-900/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="section-title">
            Software development
            <br />
            <span className="gradient-text">expertise you can trust</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            From custom software to cloud solutions, we provide end-to-end services to bring your digital vision to life.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const cardClassName =
              'group card hover:border-primary-500/30 transition-all duration-300';
            const cardContent = (
              <>
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-primary-200 dark:group-hover:bg-primary-500/20 transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {service.name}
                </h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">
                  {service.description}
                </p>
              </>
            );

            return service.href ? (
              <Link key={index} to={service.href} className={`${cardClassName} block`}>
                {cardContent}
              </Link>
            ) : (
              <div key={index} className={cardClassName}>
                {cardContent}
              </div>
            );
          })}
        </div>

        {/* View All Services */}
        <div className="text-center mt-12">
          <Link to="/services" className="btn-secondary inline-flex items-center gap-2">
            View All Services
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
